import { asset, component, defineTakeoverManifest, token } from '../../manifest-types';

import { menuSectionIds, pdpItemIds, shpIds } from './targets';

const BROWN = '#803F20';
const GROGU_GREEN = '#AFCF91';
const GROGU_GREEN_DARK = '#95B475';
const SAND = '#FDD07C';
const SAND_LIGHT = '#FDF5E6';

const ITEM_CONFIG_BASE = {
  sectionBackgroundColor: SAND_LIGHT,
};

const MEAL_ITEM_CONFIG = {
  ...ITEM_CONFIG_BASE,
  cartItemBackgroundImage: asset('mealCartItemBackground'),
  cartItemImage: asset('mealCartItem'),
};

const KIDS_ITEM_CONFIG = {
  ...ITEM_CONFIG_BASE,
  cartItemBackgroundImage: asset('kidsCartItemBackground'),
  cartItemImage: asset('kidsCartItem'),
};

const SECTION_CONFIG_BASE = {
  headerBackgroundColor: SAND,
  itemBackgroundColor: SAND_LIGHT,
  footerImage: asset('sectionFooter'),
  footerImageOffset: 60,
};

const MEALS_SECTION_CONFIG = {
  ...SECTION_CONFIG_BASE,
  sectionTileBackgroundImage: asset('mealsSectionTileBackground'),
  sectionTileImage: asset('mealsSectionTile'),
  titleMaxWidth: 125,
  sectionTileImageStyle: {
    width: 100,
    aspectRatio: 1,
  },
};

const KIDS_SECTION_CONFIG = {
  ...SECTION_CONFIG_BASE,
  sectionTileBackgroundImage: asset('kidsSectionTileBackground'),
  sectionTileImage: asset('kidsSectionTile'),
  titleMaxWidth: 125,
  sectionTileImageStyle: {
    width: 100,
    aspectRatio: 1,
  },
};

export const manifest = defineTakeoverManifest({
  id: 'mandalorian',
  ldName: 'mandalorian',
  status: 'active',
  regions: ['US', 'CA'],
  displayName: 'The Mandalorian',
  assets: {
    menuIcon: { path: 'assets/menu-icon.webp', role: 'menuIcon', status: 'final' },
    menuIconActive: {
      path: 'assets/menu-icon-active.webp',
      role: 'menuIcon.active',
      status: 'final',
    },
    mealCartItemBackground: {
      path: 'assets/meal-cart-item-background.webp',
      role: 'cart.meal.background',
      status: 'final',
    },
    mealCartItem: {
      path: 'assets/meal-cart-item.webp',
      role: 'cart.meal.image',
      status: 'final',
    },
    kidsCartItemBackground: {
      path: 'assets/kids-cart-item-background.webp',
      role: 'cart.kids.background',
      status: 'final',
    },
    kidsCartItem: {
      path: 'assets/kids-cart-item.webp',
      role: 'cart.kids.image',
      status: 'final',
    },
    sectionFooter: {
      path: 'assets/section-footer.webp',
      role: 'menuSections.footer',
      status: 'final',
    },
    mealsSectionTileBackground: {
      path: 'assets/meals-section-tile-bg.webp',
      role: 'menuSections.meals.background',
      status: 'final',
    },
    mealsSectionTile: {
      path: 'assets/meals-section-tile.webp',
      role: 'menuSections.meals.image',
      status: 'final',
    },
    kidsSectionTileBackground: {
      path: 'assets/kids-menu-section-tile-bg.webp',
      role: 'menuSections.kids.background',
      status: 'final',
    },
    kidsSectionTile: {
      path: 'assets/kids-section-tile.webp',
      role: 'menuSections.kids.image',
      status: 'final',
    },
    pdpOverviewTop: {
      path: 'assets/pdp-overview-top.webp',
      role: 'pdp.overviewTop',
      status: 'final',
    },
    pdpOverviewBottom: {
      path: 'assets/pdp-overview-bottom.webp',
      role: 'pdp.overviewBottom',
      status: 'final',
    },
    pdpSummaryPageBackground: {
      path: 'assets/pdp-summary-page-bg.webp',
      role: 'pdp.summaryBackground',
      status: 'final',
    },
    pdpHeaderFlair: {
      path: 'assets/pdp-header-flair.webp',
      role: 'pdp.headerFlair',
      status: 'final',
    },
    checkout: {
      path: 'assets/checkout.mp4',
      role: 'paymentProcessing.video',
      status: 'final',
    },
    orderConfirmHeader: {
      path: 'assets/order-confirm-details-header.webp',
      role: 'orderConfirm.header',
      status: 'final',
    },
    orderConfirmHeaderOverlay: {
      path: 'assets/order-confirm-details-header-overlay.webp',
      role: 'orderConfirm.overlay',
      status: 'final',
    },
  },
  config: {
    menuIcon: asset('menuIcon'),
    menuIconActive: asset('menuIconActive'),
    primaryActionButton: {
      backgroundColor: GROGU_GREEN,
      borderColor: GROGU_GREEN_DARK,
      borderWidth: 2,
      _text: { color: token('$bbqBrown') },
      _pressed: { backgroundColor: '#90B56C' },
    },
    homePage: {
      targetSimpleHomePageIds: shpIds,
      showReplayButton: true,
      containerBackgroundColor: BROWN,
      HeroBottomGradient: component('HeroBottomGradient', {
        endColor: BROWN,
        height: 75,
      }),
      videoHasAudio: true,
      deliveryButton: {
        _text: { color: token('$white') },
        borderColor: token('$white'),
      },
    },
    menu: {
      targetSectionGroups: [
        { ids: menuSectionIds.meals, config: MEALS_SECTION_CONFIG },
        { ids: menuSectionIds.kids, config: KIDS_SECTION_CONFIG },
      ],
    },
    pdp: {
      targetMenuItemGroups: [
        { ids: pdpItemIds.meal, config: MEAL_ITEM_CONFIG },
        { ids: pdpItemIds.kids, config: KIDS_ITEM_CONFIG },
      ],
      headerFontColor: token('$white'),
      headerBackgroundColor: '#53230B',
      backgroundColor: '#FFF8DC',
      overviewTopImage: asset('pdpOverviewTop'),
      overviewBottomImage: asset('pdpOverviewBottom'),
      overviewBottomImageOffset: 100,
      summaryPageBackgroundImage: asset('pdpSummaryPageBackground'),
      summaryPageTextColor: token('$white'),
      priceAndCaloriesFontColor: token('$bbqBrown'),
      HeaderFlair: component('HeaderFlair', {
        source: asset('pdpHeaderFlair'),
      }),
    },
    cart: {
      paymentProcessingVideo: asset('checkout'),
      paymentProcessingBackgroundColor: '#D97C31',
    },
    orderConfirmDetails: {
      headerImage: asset('orderConfirmHeader'),
      ConfirmHeaderOverlay: component('ConfirmHeaderOverlay', {
        source: asset('orderConfirmHeaderOverlay'),
      }),
      headerFontColor: token('$white'),
    },
  },
  deletion: {
    notes:
      'US + CA campaign (May 4 - Jun 4 2026), Star Wars license. To retire: remove the mandalorian entries from both branches of safe-flag-defaults.ts and LD app-takeover targeting, delete this package, regenerate the registry, and run takeover:validate --all.',
  },
});
